import { VirtualDevice, type SimulationContext } from '../VirtualDevice';
import { PIDController } from '../PIDController';

export interface PIDControllerDeviceState extends Record<string, unknown> {
  cv: number;
  pv: number;
  sp: number;
  workMode: string;
  integral: number;
}

export class PIDControllerDevice extends VirtualDevice<PIDControllerDeviceState> {
  private pid: PIDController = new PIDController();

  override reset() {
    if (!this.pid) {
      this.pid = new PIDController();
    }
    this.pid.reset();
    this.state.cv = 0;
    this.state.pv = 0;
    const workMode: import('../PIDController').PIDWorkMode = this.props['workMode'] === 'AUTO' ? 'AUTO' : 'MANUAL';
    this.state.workMode = workMode;
    this.pid.state.workMode = workMode;
    this.pid.state.manualCv = Number(this.props['manualCv'] || 0);
  }

  override update(context: SimulationContext) {
    // Sync configuration from props (so user can tune in real-time)
    this.pid.params.kp = Number(this.props['kp'] ?? 1.0);
    this.pid.params.ki = Number(this.props['ki'] ?? 0.1);
    this.pid.params.kd = Number(this.props['kd'] ?? 0.0);
    this.pid.params.sampleTimeMs = Number(this.props['sampleTimeMs'] ?? 100);
    this.pid.params.minOutput = Number(this.props['minOutput'] ?? 0);
    this.pid.params.maxOutput = Number(this.props['maxOutput'] ?? 100);
    this.pid.params.controlMode = (this.props['controlMode'] as 'P' | 'PI' | 'PID' | undefined) || 'PI';
    
    // Sync UI changes to PID state
    const targetMode = this.props['workMode'] === 'AUTO' ? 'AUTO' : 'MANUAL';
    if (this.pid.state.workMode !== targetMode) {
      this.pid.setWorkMode(targetMode);
    }
    
    // SP can be driven by a property or a PLC register
    const sp = Number(this.props['sp'] ?? 50);
    
    // Process Variable: check if sourceId is provided
    let pv = 0;
    const sourceId = this.props['sourceId'] as string;
    if (sourceId) {
      const source = context.process.getDevice(sourceId);
      if (source) {
        const s = source.state;
        if (s['signalStatus'] === 'WIRE_BREAK' || s['signalStatus'] === 'INVALID') {
          pv = Number.NaN;
        } else {
          pv = Number(s['scaledValue'] ?? s['level'] ?? s['outputValue'] ?? 0);
        }
      }
    }

    if (this.pid.state.workMode === 'MANUAL') {
      this.pid.state.manualCv = Number(this.props['manualCv'] ?? 0);
    }

    // Tick the PID algorithm
    const cv = this.pid.tick(context.simulationTimeMs, pv, sp);

    context.alarms?.evaluateCondition(
      Number.isNaN(pv) || !Number.isFinite(pv),
      this.id,
      'PID_INVALID_PV',
      'PID Invalid PV',
      'Process variable is invalid or lost.',
      'CRITICAL',
      context.simulationTimeMs
    );

    // Save states for UI / Visualization
    this.state.pv = pv;
    this.state.sp = sp;
    this.state.cv = cv;
    this.state.workMode = this.pid.state.workMode;
    this.state.integral = this.pid.state.integral;

    // Apply CV to target device
    const targetId = this.props['targetId'] as string;
    if (targetId) {
      const target = context.process.getDevice(targetId);
      if (target) {
        target.state['analogControlValue'] = cv;
      }
    }
  }
}
